import React, { useState, useMemo, useCallback } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Typography, Box, Chip, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms/States';
import { WEEK_DAYS, EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES, getChangedCellBgColor } from '../../../constants/forecast';
import EditableCell from './EditableCell';
import ViewMapTrigger from './triggers/ViewMapTrigger';
import EditWarehouseTrigger from './triggers/EditWarehouseTrigger';
import EditInfoModal from './modals/EditInfoModal';
import EditWarehouseModal from './modals/EditWarehouseModal';
import ViewZoneMapModal from './modals/ViewZoneMapModal';
import WarehouseInfo from './WarehouseInfo';
import AutoModeSwitcher from './AutoModeSwitcher';
import { createMapKey } from '../utils/transformData';
import { getForecastSourceTooltip } from '../utils/forecastSourceTooltip';

/**
 * CustomForecastTable - Renders the forecast table using Map-based data structure
 *
 * Props:
 * - editingDataMap: Map<key, itemData> - current editing state
 * - initialDataMap: Map<key, itemData> - original data for comparison
 * - changesMap: Map<key, itemData> - changed items (for highlighting)
 * - zoneInfoList: Array<{id, name, warehouse_id, address, boundary, zone_names}> - zone info for rows
 * - forecastIdByWeekDay: Map<weekDay, forecastId> - mapping of weekday to forecastId
 * - onCellChange: (mapKey, updates) => void - callback for cell value changes
 * - onUpdateZone: (dataChanges) => void - callback after zone data saved via API
 * - onUpdateInfo: (mapKey, updates) => void - callback after info data saved via API
 * - forecastByIdMap: Map<forecastId, forecast> - API forecast (is_auto_mode, percentage, source_date, source_status, ...) patched after save, read by the day header
 * - onAutoModeSaved: (patches: [{ id, is_auto_mode }]) => void - callback after auto mode saved via API
 * - timezone: string|null - selected region's timezone, used to format the tooltip dates
 */
const CustomForecastTable = ({
  editingDataMap,
  initialDataMap,
  changesMap,
  zoneInfoList,
  forecastIdByWeekDay,
  warehouses,
  onCellChange,
  onUpdateZone,
  onUpdateInfo,
  forecastByIdMap,
  timezone,
  onAutoModeSaved,
}) => {
  const theme = useTheme();
  const user = useRecoilValue(userState);
  const hasEditableScope = EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES.length === 0 || user?.user?.scopes?.some((scope) => EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES.includes(scope));

  const [editInfoModal, setEditInfoModal] = useState({ open: false, zoneId: null, weekDay: null, mapKey: null });
  const [editWarehouseModal, setEditWarehouseModal] = useState({ open: false, zoneId: null });
  const [viewMapModal, setViewMapModal] = useState({ open: false, zoneId: null, warehouse: null });

  const highlightBgColor = getChangedCellBgColor(theme);

  const allZones = useMemo(
    () =>
      zoneInfoList.map((zone) => ({
        id: zone.id,
        name: zone.name,
        boundary: zone.boundary,
      })),
    [zoneInfoList],
  );

  const getCellData = useCallback(
    (mapKey) => {
      if (!mapKey) return null;
      return editingDataMap.get(mapKey) || null;
    },
    [editingDataMap],
  );

  const getInitialCellData = useCallback(
    (mapKey) => {
      if (!mapKey) return null;
      return initialDataMap.get(mapKey) || null;
    },
    [initialDataMap],
  );

  const isCellChanged = useCallback(
    (mapKey) => {
      return changesMap.has(mapKey);
    },
    [changesMap],
  );

  const getZoneDisplayInfo = useCallback(
    (zoneId) => {
      const zoneInfo = zoneInfoList.find((z) => z.id === zoneId);

      // Find first item in editingDataMap for this zone and ticket type to get updated fields
      let editedZoneData = null;
      for (const [, item] of editingDataMap) {
        if (item.zoneId === zoneId && Array.from(forecastIdByWeekDay.values()).includes(item.forecastId)) {
          editedZoneData = {
            id: item.zoneId,
            name: item.name,
            warehouse_id: item.warehouse_id,
            address: item.address,
            boundary: item.boundary,
            zone_names: item.zone_names,
          };
          break;
        }
      }

      // If we have both, merge them with editingDataMap taking precedence
      if (zoneInfo && editedZoneData) {
        return { ...zoneInfo, ...editedZoneData };
      }

      return editedZoneData || zoneInfo || null;
    },
    [zoneInfoList, editingDataMap, forecastIdByWeekDay],
  );

  const handleCellChange = useCallback(
    (mapKey, updates) => {
      if (onCellChange && mapKey && updates) {
        onCellChange(mapKey, updates);
      }
    },
    [onCellChange],
  );

  const openEditInfo = useCallback((zoneId, weekDay, mapKey) => {
    setEditInfoModal({ open: true, zoneId, weekDay, mapKey });
  }, []);

  const closeEditInfo = useCallback(() => {
    setEditInfoModal({ open: false, zoneId: null, weekDay: null, mapKey: null });
  }, []);

  // Called after API save success - update local state
  const onSaveInfoSuccess = useCallback(
    (mapKey, formData) => {
      if (onUpdateInfo && mapKey) {
        onUpdateInfo(mapKey, formData);
      }
    },
    [onUpdateInfo],
  );

  const openEditWarehouse = useCallback((zoneId) => {
    setEditWarehouseModal({ open: true, zoneId });
  }, []);

  const closeEditWarehouse = useCallback(() => {
    setEditWarehouseModal({ open: false, zoneId: null });
  }, []);

  // Called after API save success - update local state
  const onSaveWarehouseSuccess = useCallback(
    (dataChanges) => {
      if (onUpdateZone) {
        onUpdateZone(dataChanges);
      }
    },
    [onUpdateZone],
  );

  const openViewMap = useCallback((zoneId, warehouse) => {
    setViewMapModal({ open: true, zoneId, warehouse });
  }, []);

  const closeViewMap = useCallback(() => {
    setViewMapModal({ open: false, zoneId: null, warehouse: null });
  }, []);

  // Get data for edit info modal
  const editInfoModalData = useMemo(() => {
    if (!editInfoModal.open || !editInfoModal.mapKey) return null;
    return getCellData(editInfoModal.mapKey);
  }, [editInfoModal.open, editInfoModal.mapKey, getCellData]);

  // Get data for edit warehouse modal
  const editWarehouseModalData = useMemo(() => {
    if (!editWarehouseModal.open || !editWarehouseModal.zoneId) return null;
    return getZoneDisplayInfo(editWarehouseModal.zoneId);
  }, [editWarehouseModal.open, editWarehouseModal.zoneId, getZoneDisplayInfo]);

  const warehousesMap = useMemo(() => {
    if (!warehouses) return new Map();
    return new Map(warehouses.map((w) => [w.id, w]));
  }, [warehouses]);

  return (
    <>
      <TableContainer component={Box} mt={1} mb={1}>
        <Table
          size="small"
          sx={{
            '& .MuiTableCell-root': {
              border: '1px solid rgba(224, 224, 224, 1)',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: '#f5f5f5',
                  minWidth: 150,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                }}
              >
                Zone
              </TableCell>
              {WEEK_DAYS.map((day) => {
                const forecastId = forecastIdByWeekDay.get(day);
                const forecast = forecastId ? forecastByIdMap.get(forecastId) : null;
                const initialIsAutoMode = !!forecast?.is_auto_mode;
                const percentage = forecast?.percentage ?? null;
                const showPercentage = initialIsAutoMode && percentage !== null;
                const forecastSourceTooltip = forecastId
                  ? getForecastSourceTooltip({
                      weekDay: day,
                      isAutoMode: initialIsAutoMode,
                      percentage,
                      sourceDate: forecast?.source_date ?? null,
                      sourceStatus: forecast?.source_status ?? null,
                      timezone,
                    })
                  : null;
                return (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#f5f5f5',
                      minWidth: 100,
                    }}
                  >
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                        {forecastId ? (
                          <Tooltip title={forecastSourceTooltip}>
                            <Box
                              component="span"
                              tabIndex={0}
                              aria-label={`${day}: ${forecastSourceTooltip}`}
                              sx={{
                                px: 0.8,
                                py: 0.4,
                                borderRadius: 0.5,
                                '&:hover, &:focus-visible': {
                                  bgcolor: 'action.hover',
                                },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'primary.main',
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {day}
                            </Box>
                          </Tooltip>
                        ) : (
                          <span>{day}</span>
                        )}
                        {showPercentage && <Chip size="small" label={`${percentage}%`} sx={{ fontWeight: 700, bgcolor: 'action.hover' }} />}
                      </Stack>
                      {forecastId && <AutoModeSwitcher key={forecastId} forecastId={forecastId} initialIsAutoMode={initialIsAutoMode} onSaveSuccess={onAutoModeSaved} />}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {zoneInfoList.map((zone) => {
              const zoneDisplayInfo = getZoneDisplayInfo(zone.id) || zone;
              const warehouse = warehousesMap.get(zoneDisplayInfo.warehouse_id);

              return (
                <TableRow key={zone.id}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight: 600,
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'white',
                      zIndex: 1,
                    }}
                  >
                    <Stack direction="column" sx={{ gap: 1, justifyContent: 'space-between' }}>
                      <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {zoneDisplayInfo.name}
                        </Typography>
                        <ViewMapTrigger onOpen={() => openViewMap(zone.id, warehouse)} />
                      </Stack>
                      <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <WarehouseInfo data={warehouse} id={zoneDisplayInfo.warehouse_id} />
                        {hasEditableScope && <EditWarehouseTrigger onOpen={() => openEditWarehouse(zone.id)} />}
                      </Stack>
                    </Stack>
                  </TableCell>
                  {WEEK_DAYS.map((weekDay) => {
                    const forecastId = forecastIdByWeekDay.get(weekDay);

                    if (!forecastId) {
                      return (
                        <TableCell key={`${zone.id}-${weekDay}`} align="center">
                          -
                        </TableCell>
                      );
                    }

                    const mapKey = createMapKey(forecastId, weekDay, zone.id);
                    const cellData = getCellData(mapKey);
                    const initialCellData = getInitialCellData(mapKey);
                    const isChanged = isCellChanged(mapKey);

                    return (
                      <EditableCell
                        key={`${zone.id}-${weekDay}`}
                        mapKey={mapKey}
                        cellData={cellData}
                        initialCellData={initialCellData}
                        isChanged={isChanged}
                        highlightColor={highlightBgColor}
                        isAutoMode={!!forecastByIdMap.get(forecastId)?.is_auto_mode}
                        timezone={timezone}
                        onDataChange={handleCellChange}
                        onOpenEditInfo={() => openEditInfo(zone.id, weekDay, mapKey)}
                      />
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}
      <EditInfoModal open={editInfoModal.open} onClose={closeEditInfo} data={editInfoModalData} mapKey={editInfoModal.mapKey} onSaveSuccess={onSaveInfoSuccess} />

      <EditWarehouseModal open={editWarehouseModal.open} onClose={closeEditWarehouse} data={editWarehouseModalData} zoneId={editWarehouseModal.zoneId} onSaveSuccess={onSaveWarehouseSuccess} />

      <ViewZoneMapModal open={viewMapModal.open} onClose={closeViewMap} currentZoneId={viewMapModal.zoneId} currentWarehouse={viewMapModal.warehouse} allZones={allZones} />
    </>
  );
};

export default CustomForecastTable;
